import { useState, useRef, useEffect, useCallback } from 'react';
import { Share2, Twitter, Facebook, Linkedin, MessageCircle, Mail, Link2, Check } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';

const ShareContainer = styled.div`
  position: relative;
`;

const ShareButton = styled(Button)`
  gap: 0.5rem;
`;

const ShareDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background-color: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.elevated};
  padding: 0.5rem;
  min-width: 12rem;
  z-index: 50;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  
  @media (min-width: 1024px) {
    min-width: 14rem;
  }
`;

const ShareOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: none;
  background: none;
  color: ${props => props.theme.colors.foreground};
  font-size: 0.9375rem;
  text-align: left;
  cursor: pointer;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: background-color 0.2s;
  
  @media (min-width: 1024px) {
    font-size: 1rem;
    padding: 0.875rem;
  }
  
  &:hover {
    background-color: ${props => props.theme.colors.muted};
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }
`;

const ShareButtons = ({ title, url, description }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef(null);

  const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;
  const shareText = `${title} - ${description || ''}`;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const copyLink = useCallback(async () => {
    const fallbackCopy = () => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = fullUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch {
        return false;
      }
    };

    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullUrl);
        ok = true;
      } else {
        ok = fallbackCopy();
      }
    } catch {
      ok = fallbackCopy();
    }

    if (ok) {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setIsOpen(false);
      }, 2000);
    } else {
      setIsOpen(false);
    }
  }, [fullUrl]);

  const openPopup = useCallback((shareUrl) => {
    window.open(shareUrl, '_blank', 'width=550,height=420,noopener,noreferrer');
    setIsOpen(false);
  }, []);

  const shareOptions = [
    {
      name: 'Twitter',
      label: 'Twitter',
      icon: Twitter,
      color: '#1DA1F2',
      onClick: () =>
        openPopup(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(fullUrl)}`
        ),
    },
    {
      name: 'Facebook',
      label: 'Facebook',
      icon: Facebook,
      color: '#1877F2',
      onClick: () =>
        openPopup(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`),
    },
    {
      name: 'LinkedIn',
      label: 'LinkedIn',
      icon: Linkedin,
      color: '#0077B5',
      onClick: () =>
        openPopup(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`),
    },
    {
      name: 'WhatsApp',
      label: 'WhatsApp',
      icon: MessageCircle,
      color: '#25D366',
      onClick: () =>
        openPopup(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${fullUrl}`)}`),
    },
    {
      name: 'Email',
      label: t('article.shareEmail'),
      icon: Mail,
      color: '#666',
      onClick: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${shareText}\n\n${fullUrl}`)}`;
        setIsOpen(false);
      },
    },
    {
      name: 'Copy Link',
      label: copied ? t('article.linkCopied') : t('article.copyLink'),
      icon: copied ? Check : Link2,
      color: copied ? '#16a34a' : '#666',
      onClick: copyLink,
    },
  ];

  return (
    <ShareContainer ref={containerRef}>
      <ShareButton
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={t('article.shareArticle')}
      >
        <Share2 />
        {t('article.share')}
      </ShareButton>
      <ShareDropdown $isOpen={isOpen} role="menu">
        {shareOptions.map((option) => {
          const Icon = option.icon;
          return (
            <ShareOption
              key={option.name}
              onClick={option.onClick}
              role="menuitem"
              aria-label={t('article.shareOn', { platform: option.label })}
            >
              <Icon style={{ color: option.color }} />
              <span>{option.label}</span>
            </ShareOption>
          );
        })}
      </ShareDropdown>
    </ShareContainer>
  );
};

export default ShareButtons;
